import { Box, Button, Skeleton, styled, Typography } from '@mui/material';
import theme from 'src/theme';

export const RootBox = styled(Box)(({ theme: muiTheme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: muiTheme.spacing(10),
    marginBottom: muiTheme.spacing(10),
    marginLeft: muiTheme.spacing(2),
    marginRight: muiTheme.spacing(2),
    [muiTheme.breakpoints.up('md')]: {
        marginLeft: muiTheme.spacing(20),
        marginRight: muiTheme.spacing(20)
    }
}));

export const PageHeader = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
`;

export const PageTitle = styled(Typography)`
    font-size: 24px;
    font-weight: 600;
    color: ${theme.color.white};
`;

export const BackButton = styled(Button)`
    color: ${theme.color.white};
    border: 1px solid ${theme.color.white50};
    text-transform: none;
    &:hover {
        border-color: ${theme.color.white};
        background-color: rgba(255, 255, 255, 0.04);
    }
`;

export const Layout = styled(Box)`
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 16px;
    align-items: start;
`;

/* ──────────────────────────── Session list ──────────────────────────── */

export const SessionListPanel = styled(Box)`
    background-color: ${theme.color.blackRock};
    border-radius: 4px;
    padding: 12px;
    max-height: 75vh;
    overflow-y: auto;
`;

export const SessionCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active'
})<{ active?: boolean }>`
    background-color: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
    border: 1px solid
        ${({ active }) => (active ? theme.color.white50 : 'transparent')};
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 8px;
    cursor: pointer;
    transition:
        background-color 120ms ease,
        border-color 120ms ease;
    &:hover {
        background-color: rgba(255, 255, 255, 0.04);
    }
`;

export const SessionType = styled(Typography)`
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    color: ${theme.color.lightningYellow};
    margin-bottom: 4px;
    letter-spacing: 0.02em;
`;

export const SessionId = styled(Typography)`
    font-size: 12px;
    color: ${theme.color.white};
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    word-break: break-all;
`;

export const SessionMeta = styled(Typography)`
    font-size: 11px;
    color: ${theme.color.white50};
    margin-top: 4px;
`;

/* ──────────────────────────── Transcript shell ──────────────────────────── */

export const TranscriptPanel = styled(Box)`
    background-color: ${theme.color.blackRock};
    border-radius: 4px;
    padding: 16px;
    max-height: 75vh;
    overflow-y: auto;
`;

export const TranscriptHeader = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    margin-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

export const TranscriptHeaderLeft = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const TranscriptSessionLabel = styled(Typography)`
    font-size: 13px;
    color: ${theme.color.white};
`;

export const TranscriptSessionId = styled('span')`
    font-family: 'SF Mono', Menlo, Consolas, monospace;
`;

export const TranscriptCost = styled(Typography)`
    font-size: 12px;
    color: ${theme.color.white50};
`;

export const NoTranscriptHint = styled(Typography)`
    font-size: 12px;
    color: ${theme.color.white50};
    margin-bottom: 8px;
`;

export const TranscriptStream = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

/* ──────────────────────────── Message blocks ──────────────────────────── */

export type MessageRole = 'user' | 'assistant' | 'system' | 'tool' | 'unknown';

const roleAccent = (role: MessageRole) => {
    switch (role) {
        case 'user':
            return '#60a5fa';
        case 'assistant':
            return '#a78bfa';
        case 'tool':
            return '#f59e0b';
        case 'system':
            return '#9ca3af';
        default:
            return '#4b5563';
    }
};

export const MessageBlock = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'role'
})<{ role: MessageRole }>`
    border-left: 3px solid ${({ role }) => roleAccent(role)};
    background-color: rgba(255, 255, 255, 0.02);
    border-radius: 0 6px 6px 0;
    overflow: hidden;
`;

export const MessageHeader = styled('button', {
    shouldForwardProp: (prop) => prop !== 'role'
})<{ role: MessageRole }>`
    appearance: none;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    color: ${theme.color.white};
    &:hover {
        background-color: rgba(255, 255, 255, 0.03);
    }
`;

export const MessageRoleLabel = styled(Typography)`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${theme.color.white50};
`;

export const MessagePreview = styled('span')`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const MessageChevron = styled('span', {
    shouldForwardProp: (prop) => prop !== 'open'
})<{ open: boolean }>`
    color: rgba(255, 255, 255, 0.5);
    font-size: 10px;
    transition: transform 120ms ease;
    transform: rotate(${({ open }) => (open ? 90 : 0)}deg);
    flex-shrink: 0;
    margin-left: auto;
`;

export const MessageBody = styled(Box)`
    padding: 4px 14px 10px;
`;

export const MessageText = styled(Box)`
    font-size: 13px;
    color: ${theme.color.white};
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.55;
`;

/* ──────────────────────────── Thinking block ──────────────────────────── */

export const ThinkingBlock = styled(Box)`
    background-color: rgba(167, 139, 250, 0.06);
    border: 1px dashed rgba(167, 139, 250, 0.35);
    border-radius: 6px;
    padding: 8px 12px;
    margin-top: 6px;
`;

export const ThinkingHeader = styled('button')`
    appearance: none;
    background: none;
    border: none;
    color: rgba(167, 139, 250, 0.9);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

export const ThinkingBody = styled(Box)`
    margin-top: 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
    white-space: pre-wrap;
    line-height: 1.55;
`;

/* ──────────────────────────── Tool card ──────────────────────────── */

export type ToolKind =
    | 'bash'
    | 'read'
    | 'edit'
    | 'write'
    | 'grep'
    | 'glob'
    | 'web-fetch'
    | 'web-search'
    | 'agent'
    | 'todo'
    | 'generic';

const toolAccent = (kind: ToolKind) => {
    switch (kind) {
        case 'bash':
            return '#22d3ee';
        case 'read':
            return '#34d399';
        case 'edit':
            return '#fbbf24';
        case 'write':
            return '#f97316';
        case 'grep':
        case 'glob':
            return '#a78bfa';
        case 'web-fetch':
        case 'web-search':
            return '#60a5fa';
        case 'agent':
            return '#f472b6';
        case 'todo':
            return '#a3e635';
        default:
            return '#94a3b8';
    }
};

export const ToolCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'kind'
})<{ kind: ToolKind }>`
    border: 1px solid ${({ kind }) => toolAccent(kind)}40;
    background-color: ${({ kind }) => toolAccent(kind)}0d;
    border-radius: 6px;
    overflow: hidden;
`;

export const ToolHeader = styled('button', {
    shouldForwardProp: (prop) => prop !== 'kind'
})<{ kind: ToolKind }>`
    appearance: none;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid ${({ kind }) => toolAccent(kind)}30;
    color: ${theme.color.white};
    &:hover {
        background-color: rgba(255, 255, 255, 0.03);
    }
`;

export const ToolIcon = styled('span', {
    shouldForwardProp: (prop) => prop !== 'kind'
})<{ kind: ToolKind }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    background-color: ${({ kind }) => toolAccent(kind)}33;
    color: ${({ kind }) => toolAccent(kind)};
    font-size: 12px;
    font-weight: 700;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    flex-shrink: 0;
`;

export const ToolName = styled('span')`
    font-size: 12px;
    font-weight: 600;
    color: ${theme.color.white};
    flex-shrink: 0;
`;

export const ToolSummary = styled('span')`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`;

export const ToolChevron = styled('span', {
    shouldForwardProp: (prop) => prop !== 'open'
})<{ open: boolean }>`
    color: rgba(255, 255, 255, 0.5);
    font-size: 10px;
    transition: transform 120ms ease;
    transform: rotate(${({ open }) => (open ? 90 : 0)}deg);
    flex-shrink: 0;
`;

export const ToolBody = styled(Box)`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: ${theme.color.white};
    font-size: 12px;
`;

export const ToolSection = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const ToolSectionLabel = styled(Typography)`
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.5);
`;

export const FilePath = styled('code')`
    display: inline-block;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 12px;
    color: ${theme.color.white};
    background-color: rgba(255, 255, 255, 0.06);
    padding: 2px 6px;
    border-radius: 3px;
    word-break: break-all;
`;

export const CommandLine = styled('pre')`
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 12px;
    color: #22d3ee;
    background-color: rgba(0, 0, 0, 0.35);
    padding: 8px 12px;
    border-radius: 4px;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    &::before {
        content: '$ ';
        color: rgba(255, 255, 255, 0.4);
    }
`;

export const MonoBlock = styled('pre')`
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 11.5px;
    color: ${theme.color.white};
    background-color: rgba(0, 0, 0, 0.3);
    padding: 8px 12px;
    border-radius: 4px;
    margin: 0;
    max-height: 360px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
`;

export const DiffBlock = styled('pre')`
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 11.5px;
    background-color: rgba(0, 0, 0, 0.3);
    padding: 8px 12px;
    border-radius: 4px;
    margin: 0;
    max-height: 360px;
    overflow: auto;
    line-height: 1.5;
`;

export const DiffOld = styled('div')`
    color: #fca5a5;
    background-color: rgba(239, 68, 68, 0.08);
    &::before {
        content: '- ';
        color: rgba(252, 165, 165, 0.6);
    }
`;

export const DiffNew = styled('div')`
    color: #86efac;
    background-color: rgba(34, 197, 94, 0.08);
    &::before {
        content: '+ ';
        color: rgba(134, 239, 172, 0.6);
    }
`;

export const TodoList = styled('ul')`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const TodoItem = styled('li', {
    shouldForwardProp: (prop) => prop !== 'status'
})<{ status: 'pending' | 'in_progress' | 'completed' | 'unknown' }>`
    font-size: 12px;
    color: ${({ status }) =>
        status === 'completed'
            ? 'rgba(255,255,255,0.4)'
            : status === 'in_progress'
              ? '#fbbf24'
              : theme.color.white};
    text-decoration: ${({ status }) =>
        status === 'completed' ? 'line-through' : 'none'};
    display: flex;
    align-items: flex-start;
    gap: 6px;
    &::before {
        content: ${({ status }) =>
            status === 'completed'
                ? "'\\2713'"
                : status === 'in_progress'
                  ? "'\\25B6'"
                  : "'\\2022'"};
        color: ${({ status }) =>
            status === 'completed'
                ? '#86efac'
                : status === 'in_progress'
                  ? '#fbbf24'
                  : 'rgba(255,255,255,0.4)'};
        flex-shrink: 0;
    }
`;

export const ResultLabel = styled(Typography)`
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.45);
    margin-bottom: 4px;
`;

export const ResultError = styled('span')`
    color: #fca5a5;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-left: 6px;
`;

/* ──────────────────────────── Raw fallback ──────────────────────────── */

export const RawJson = styled('pre')`
    font-size: 11px;
    color: ${theme.color.white};
    background-color: rgba(0, 0, 0, 0.3);
    padding: 8px 12px;
    border-radius: 4px;
    overflow: auto;
    margin: 0;
    max-height: 360px;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    white-space: pre-wrap;
    word-break: break-word;
`;

/* ──────────────────────────── Empty / loading ──────────────────────────── */

export const EmptyState = styled(Typography)`
    color: ${theme.color.white50};
    text-align: center;
    padding: 24px;
`;

export const PanelSkeleton = styled(Skeleton)`
    background-color: rgba(255, 255, 255, 0.06);
`;

export const SkeletonRow = styled(PanelSkeleton)`
    margin-bottom: 8px;
`;
